import React, { useState, useEffect } from "react";
import { CreditCard, Calendar, Tag, Award } from "lucide-react";

export default function MembershipCardComponent({ data }) {
  const [membershipInfo, setMembershipInfo] = useState({
    membership_card_name: "",
    membership_card_no: "",
    annual_fees: 0,
    discount_percentage: 0,
    valid_till: "",
    patient_name: "",
  });

  useEffect(() => {
    const membershipData = data?.memberships_orders?.[0];

    if (membershipData) {
      setMembershipInfo({
        membership_card_name:
          membershipData?.membership_obj?.membership_card_name || "",
        membership_card_no: membershipData?.membership_card_no || "",
        annual_fees: membershipData?.annual_fees || 0,
        discount_percentage: membershipData?.discount_percentage || 0,
        valid_till: membershipData?.valid_till || "",
        patient_name: membershipData?.patient_name || "",
      });
    }
  }, [data]);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const getCardClass = (cardType) => {
    const classes = {
      GOLDEN: "bg-warning",
      SILVER: "bg-secondary",
      PLATINUM: "bg-dark",
    };
    return classes[cardType] || "bg-warning";
  };

  if (
    !membershipInfo.membership_card_name &&
    !membershipInfo.membership_card_no
  ) {
    return null;
  }

  return (
    <div className="w-100" style={{ maxWidth: "28rem", margin: "0 auto" }}>
      <div
        className={`card ${getCardClass(
          membershipInfo.membership_card_name,
        )} text-white shadow-lg position-relative overflow-hidden`}
        style={{
          borderRadius: "1rem",
          transition: "transform 0.3s ease",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = "scale(1.02)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "scale(1)";
        }}
      >
        <div
          className="position-absolute"
          style={{
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            opacity: 0.1,
            pointerEvents: "none",
          }}
        >
          <div
            className="bg-white rounded-circle position-absolute"
            style={{
              width: "16rem",
              height: "16rem",
              top: "-8rem",
              right: "-8rem",
            }}
          ></div>
          <div
            className="bg-white rounded-circle position-absolute"
            style={{
              width: "12rem",
              height: "12rem",
              bottom: "-6rem",
              left: "-6rem",
            }}
          ></div>
        </div>

        <div className="card-body p-4 position-relative">
          <div className="d-flex justify-content-between align-items-start mb-4">
            <div>
              {membershipInfo.membership_card_name && (
                <>
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <Award size={24} className="text-white" />
                    <h2
                      className="h4 mb-0 fw-bold text-white"
                      style={{ letterSpacing: "0.05em" }}
                    >
                      {membershipInfo.membership_card_name}
                    </h2>
                  </div>
                  <p
                    className="small mb-0 fw-semibold"
                    style={{ color: "rgba(255,255,255,0.8)" }}
                  >
                    MEMBERSHIP CARD
                  </p>
                </>
              )}
            </div>
            <CreditCard size={40} style={{ color: "rgba(255,255,255,0.9)" }} />
          </div>

          <div className="d-flex gap-3 mb-4">
            {membershipInfo.membership_card_no && (
              <div style={{ flex: 1 }}>
                <p
                  className="small text-uppercase mb-2"
                  style={{
                    color: "rgba(255,255,255,0.7)",
                    letterSpacing: "0.05em",
                  }}
                >
                  Card Number
                </p>
                <p
                  className="h4 mb-0 fw-bold"
                  style={{
                    fontFamily: "monospace",
                    letterSpacing: "0.1em",
                  }}
                >
                  {membershipInfo.membership_card_no}
                </p>
              </div>
            )}

            {membershipInfo.patient_name && (
              <div style={{ flex: 1 }}>
                <p
                  className="small text-uppercase mb-2"
                  style={{
                    color: "rgba(255,255,255,0.7)",
                    letterSpacing: "0.05em",
                  }}
                >
                  Card Holder
                </p>
                <p
                  className="h5 mb-0 fw-bold"
                  style={{ letterSpacing: "0.05em" }}
                >
                  {membershipInfo.patient_name}
                </p>
              </div>
            )}
          </div>

          <div className="row g-3 mb-3">
            {membershipInfo.valid_till && (
              <div className="col-6">
                <div className="d-flex align-items-center gap-2 mb-2">
                  <Calendar
                    size={16}
                    style={{ color: "rgba(255,255,255,0.8)" }}
                  />
                  <p
                    className="small text-uppercase mb-0"
                    style={{
                      color: "rgba(255,255,255,0.7)",
                      letterSpacing: "0.05em",
                    }}
                  >
                    Valid Till
                  </p>
                </div>
                <p className="h6 mb-0 fw-semibold">
                  {formatDate(membershipInfo.valid_till)}
                </p>
              </div>
            )}

            {membershipInfo.discount_percentage > 0 && (
              <div className="col-6">
                <div className="d-flex align-items-center gap-2 mb-2">
                  <Tag size={16} style={{ color: "rgba(255,255,255,0.8)" }} />
                  <p
                    className="small text-uppercase mb-0"
                    style={{
                      color: "rgba(255,255,255,0.7)",
                      letterSpacing: "0.05em",
                    }}
                  >
                    Discount
                  </p>
                </div>
                <p className="h6 mb-0 fw-semibold">
                  {membershipInfo.discount_percentage}% OFF
                </p>
              </div>
            )}
          </div>

          {membershipInfo.annual_fees > 0 && (
            <div
              className="pt-3 mt-3"
              style={{ borderTop: "1px solid rgba(255,255,255,0.2)" }}
            >
              <div className="d-flex justify-content-between align-items-center">
                <p
                  className="small text-uppercase mb-0"
                  style={{
                    color: "rgba(255,255,255,0.7)",
                    letterSpacing: "0.05em",
                  }}
                >
                  Annual Fees
                </p>
                <p className="h5 mb-0 fw-bold">
                  ₹{membershipInfo.annual_fees.toLocaleString("en-IN")}
                </p>
              </div>
            </div>
          )}
        </div>

        <div
          className="position-absolute"
          style={{
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            background:
              "linear-gradient(135deg, transparent, rgba(255,255,255,0.1), transparent)",
            opacity: 0,
            transition: "opacity 0.5s ease",
            pointerEvents: "none",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.opacity = "1";
          }}
        ></div>
      </div>
    </div>
  );
}
